import { SOVEREIGN_JEWEL_DAMAGE_BPS, toExactBigInt } from '@kenocyber/shared';
import { DAMAGE_MILLI_PER_UNIT } from './base';

/**
 * Exact damage arithmetic, and the one place it stops being exact.
 *
 * ## What is exact, and why it has to be
 *
 * Every function here works in `bigint`. Not because damage is usually large -
 * it is usually 40 - but because one input is unbounded by product decision:
 * each incorporated Primordial Heart adds 100 basis points and they accumulate
 * with no gameplay ceiling
 * (`docs/gates/v28d2-primordial-ownership.md`). A count stored in
 * `numeric(40, 0)` multiplied by a base produces a number with more digits than
 * a `double` can hold, and the moment it is converted the game has silently
 * capped an item's effect - which is the one thing `CLAUDE.md` forbids
 * absolutely.
 *
 * ## What is NOT migrated to `bigint`, deliberately
 *
 * The physics. Positions, velocities, health and the tick are what they were:
 * integers in the duel, floats in the hunt. Rewriting them would be a change to
 * every number in the game to solve a problem that exists in exactly one place.
 *
 * The boundary between the two worlds is {@link resolveStrike}, and it is the
 * only function here that returns a `number`. It is where an exact,
 * arbitrarily large damage meets a bounded remaining health - and it crosses by
 * COMPARING rather than converting, so the large value never becomes a
 * `double` at all.
 *
 * ## Where it IS wired, since the gate V28F.B
 *
 * {@link shotDamageMilli} decides the damage of every ordinary shot in both
 * modes, and {@link resolveStrike} is the boundary every blow crosses. The
 * jewel functions below still have no production caller - no jewel exists - and
 * that stays true: the only summand that reaches `summedBps` today is
 * {@link primordialHeartBps}.
 *
 * A `bigint` per shot is a real cost, and it is paid where it buys something: a
 * shot is born a handful of times a second per ship, not per tick and not per
 * substep. The physics loop - moving, colliding, expiring - never touches one.
 */

/** 10 000 basis points is one whole. The unit these coefficients are in. */
export const BPS_ONE = 10_000n;

/** Milli-damage per whole point, as a `bigint` for the exact side. */
const MILLI = BigInt(DAMAGE_MILLI_PER_UNIT);

/** One second, in milliseconds, for the periodic arithmetic. */
const MS_PER_SECOND = 1000n;

function exact(value: bigint | number, label: string): bigint {
  return toExactBigInt(value, label);
}

/* -------------------------------------------------------------------------- */
/*  The normal shot                                                           */
/* -------------------------------------------------------------------------- */

/**
 * What N incorporated Primordial Hearts are worth, in basis points.
 *
 * `100 x count`, exact, and that is the whole formula (ADR 0050). One heart is
 * +1 % of the ordinary shot's base; a hundred are +100 %; `10^40 - 1` are a
 * number with forty-two digits, and it stays a number with forty-two digits.
 *
 * ## Why it is a function and not an inline multiplication
 *
 * Because it is the ONE place a count becomes a percentage, and because being a
 * function is what lets a proof aim at it. Two call sites - the duel and the
 * hunt - would otherwise each carry a `100n *`, and the day a design changes
 * that constant one of them would keep the old one.
 *
 * ## What may never be passed here
 *
 * A count of hearts a player merely OWNS. This takes the count incorporated
 * into a SHIP, read from `ship_instances.primordial_hearts` and frozen into the
 * loadout or the combat profile. Possession grants nothing, and there is no
 * parameter through which possession could reach this function.
 */
export function primordialHeartBps(count: bigint | number): bigint {
  const hearts = exact(count, 'primordial heart count');
  if (hearts < 0n) {
    throw new RangeError(`a primordial heart count is never negative, got ${hearts}`);
  }
  return hearts * BigInt(SOVEREIGN_JEWEL_DAMAGE_BPS);
}

/**
 * Step 2 of the canonical order: the SUMMED percentages, applied once.
 *
 * `base x (10 000 + summed) / 10 000`, truncated once at the end.
 *
 * Everything that modifies the ordinary shot goes into `summedBps` as an
 * addend - the ship's own bonus, the artifacts, the equipment, the Chama da
 * Destruição, and 100 per incorporated Primordial Heart. Additive, never
 * multiplicative: `+900` and `+2500` are `+3400`, never `1,09 x 1,25`. That is
 * what stops a heart from multiplying a jewel and a jewel from multiplying a
 * heart, and it is why they can share one parameter at all.
 *
 * A negative sum is refused rather than clamped. Nothing grants one today, and
 * a percentage that took a shot below zero would be a subtraction wearing a
 * bonus's name.
 */
export function shotDamageMilli(baseMilli: bigint | number, summedBps: bigint | number): bigint {
  const base = exact(baseMilli, 'base damage in milli');
  const summed = exact(summedBps, 'summed damage bps');
  if (base < 0n) {
    throw new RangeError('base damage must not be negative');
  }
  if (BPS_ONE + summed < 0n) {
    throw new RangeError(`summed damage bps must not take a shot below zero: ${summed}`);
  }
  return (base * (BPS_ONE + summed)) / BPS_ONE;
}

/* -------------------------------------------------------------------------- */
/*  What a jewel emits                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Damage a jewel emits: a coefficient of the BASE, and of nothing else.
 *
 * Deliberately a different function from {@link shotDamageMilli} even though
 * the arithmetic is one multiplication apart. They answer different questions -
 * "what does the gun do now" versus "what is this effect worth" - and the whole
 * anti-feedback rule is that the second never reads the first. One shared
 * function would be one place a future edit could make it.
 */
export function jewelDamageMilli(baseMilli: bigint | number, coefBps: bigint | number): bigint {
  const base = exact(baseMilli, 'base damage in milli');
  const coef = exact(coefBps, 'jewel coefficient bps');
  if (base < 0n || coef < 0n) {
    throw new RangeError('a jewel coefficient and its base are both non-negative');
  }
  return (base * coef) / BPS_ONE;
}

/**
 * Healing a jewel emits, on the same terms.
 *
 * Its own function for the same reason: the Orbe Vampírico and the Coração
 * Vital heal a coefficient of the base and **never** of the damage actually
 * applied, of a critical, of a DoT or of another jewel's output. Sharing a name
 * with damage would invite a call site to pass an `applied` it happened to have.
 */
export function jewelHealMilli(baseMilli: bigint | number, coefBps: bigint | number): bigint {
  return jewelDamageMilli(baseMilli, coefBps);
}

/* -------------------------------------------------------------------------- */
/*  Marks                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What a detonation is worth: the sum of what the marks are carrying.
 *
 * Each mark records the base damage that was live when it was APPLIED (the
 * boundary rule of the contract), so a detonation is a plain sum rather than
 * `count x current base`. The two agree while the base has not moved, and only
 * this one keeps agreeing after a player equips something mid-fight.
 *
 * Takes the total already summed by the caller's counter, which is what a
 * bucket holds - never a list of mark objects, because `sem objeto por marca` is
 * a performance property the contract states.
 */
export function markTotalMilli(perMarkMilli: bigint | number, marks: bigint | number): bigint {
  const per = exact(perMarkMilli, 'per-mark damage in milli');
  const count = exact(marks, 'mark count');
  if (per < 0n || count < 0n) {
    throw new RangeError('marks and their value are both non-negative');
  }
  return per * count;
}

/* -------------------------------------------------------------------------- */
/*  Periodic damage                                                           */
/* -------------------------------------------------------------------------- */

/** What one DoT tick produced, and what it owes the next one. */
export interface DotTick {
  /** Whole damage points to apply now. */
  readonly whole: bigint;
  /**
   * The remainder, in milli-damage-MILLISECONDS.
   *
   * The finest unit in the calculation, and one carry rather than two. A
   * coarser carry would have to round somewhere, and the whole point of a carry
   * is that nothing is rounded: `4 320 000` here is the `320` milli the
   * contract's worked example names, times the thousand milliseconds it was
   * measured over.
   */
  readonly carryMilliMs: bigint;
}

/**
 * One tick of periodic damage, carrying its remainder exactly.
 *
 * `perStackPerSecondMilli x stacks x periodMs` is milli-damage-milliseconds;
 * dividing by 1 000 000 gives whole damage, and the remainder is kept rather
 * than dropped. It is the same shape as the hunt's `regenCarryMilli`, which is
 * already persisted in the snapshot for exactly this reason - a fraction thrown
 * away every tick is a fraction thrown away twenty times a second.
 *
 * Stacks are a `bigint` and are not capped here. The Orbe da Víbora accumulates
 * without a gameplay ceiling, so a ceiling in the arithmetic would be a
 * gameplay cap in disguise.
 */
export function dotTickWithCarry(input: {
  readonly perStackPerSecondMilli: bigint | number;
  readonly stacks: bigint | number;
  readonly periodMs: bigint | number;
  readonly carryMilliMs: bigint | number;
}): DotTick {
  const perSecond = exact(input.perStackPerSecondMilli, 'per-stack damage per second in milli');
  const stacks = exact(input.stacks, 'stack count');
  const periodMs = exact(input.periodMs, 'dot period in ms');
  const carry = exact(input.carryMilliMs, 'dot carry in milli-ms');
  if (perSecond < 0n || stacks < 0n || periodMs < 0n || carry < 0n) {
    throw new RangeError('a periodic tick has no negative term');
  }
  const numerator = perSecond * stacks * periodMs + carry;
  const denominator = MILLI * MS_PER_SECOND;
  return { whole: numerator / denominator, carryMilliMs: numerator % denominator };
}

/**
 * Milli-damage to whole damage, keeping the fraction.
 *
 * The simpler half of the same idea, for an effect that already knows its
 * milli. `4 320` milli with no carry is `4` damage owing `320` - the number the
 * contract's poison example prints.
 */
export function wholeFromMilliWithCarry(
  amountMilli: bigint | number,
  carryMilli: bigint | number,
): { readonly whole: bigint; readonly carryMilli: bigint } {
  const amount = exact(amountMilli, 'amount in milli');
  const carry = exact(carryMilli, 'carry in milli');
  if (amount < 0n || carry < 0n) {
    throw new RangeError('a milli amount and its carry are both non-negative');
  }
  const total = amount + carry;
  return { whole: total / MILLI, carryMilli: total % MILLI };
}

/* -------------------------------------------------------------------------- */
/*  The boundary                                                              */
/* -------------------------------------------------------------------------- */

/** What an exact blow does to a hull the simulation describes with a `number`. */
export interface StrikeResolution {
  /** True when the blow is worth at least the whole remaining health. */
  readonly lethal: boolean;
  /**
   * The damage to apply, in the SIMULATION's own unit - never more than the
   * remaining health, so a caller's `Math.min` is a formality rather than a
   * defence.
   */
  readonly applied: number;
  /**
   * Whether the exact value had to be converted at all.
   *
   * `false` on a lethal blow, always. It is asserted by the tests rather than
   * merely documented, because "the big product never becomes a double" is the
   * property this whole module exists to hold.
   */
  readonly converted: boolean;
}

const NOTHING: StrikeResolution = Object.freeze({ lethal: false, applied: 0, converted: false });

/**
 * **The frontier between exact arithmetic and the running simulation.**
 *
 * ## How an unbounded damage meets a bounded hull without overflowing
 *
 * By comparison, not conversion. The remaining health is scaled UP into milli
 * and rounded away from zero, giving an exact `bigint` ceiling that a `double`
 * can always express. Then:
 *
 * - `damageMilli >= ceiling` - the blow is worth at least the whole hull, so it
 *   is **lethal** and applies exactly the remaining health. How much larger it
 *   was is not a question anybody needs answered: overkill has never been
 *   counted in either simulation. **Nothing is converted.**
 * - otherwise `damageMilli` is strictly below a ceiling that already fits, so
 *   converting it is safe by proof rather than by hope.
 *
 * A blow of `10^38` and a blow of `10^9` kill a hull on 40 health identically,
 * which is why a Primordial Heart count needs no ceiling: the product is never
 * required to exist as a `number`.
 *
 * ## Never
 *
 * `Infinity`, `NaN`, a wrap, a saturation, or a frozen multiplier. There is no
 * clamp on the damage anywhere in this function - only a clamp on what a hull
 * can lose, which is a different statement and a true one.
 *
 * @param remainingHealth the hull's health right now, in the mode's own unit.
 *   Integer in the duel, possibly fractional in the hunt; both work, because
 *   the ceiling is computed with `Math.ceil` rather than assumed whole.
 */
export function resolveStrike(damageMilli: bigint, remainingHealth: number): StrikeResolution {
  if (!Number.isFinite(remainingHealth)) {
    throw new RangeError(`remaining health must be finite, got ${remainingHealth}`);
  }
  if (remainingHealth <= 0 || damageMilli <= 0n) {
    // A hull already down absorbs nothing, and a blow worth nothing takes
    // nothing. Neither emits, and neither converts.
    return NOTHING;
  }
  const ceilingMilli = BigInt(Math.ceil(remainingHealth * DAMAGE_MILLI_PER_UNIT));
  if (damageMilli >= ceilingMilli) {
    return { lethal: true, applied: remainingHealth, converted: false };
  }
  /*
   * Proven small: strictly below a ceiling that is itself a safe integer, so
   * this conversion cannot lose a digit. It is the only one in the module.
   */
  return {
    lethal: false,
    applied: Number(damageMilli) / DAMAGE_MILLI_PER_UNIT,
    converted: true,
  };
}

/** What an exact blow does to a hull whose health is a whole number. */
export interface IntegerStrikeResolution {
  readonly lethal: boolean;
  /** Whole health points to remove now. Never more than what remains. */
  readonly applied: number;
  /**
   * What this blow could not spend, in milli, for the NEXT blow on this hull.
   *
   * Always in `[0, 999]` on a survivor, and `0` on a lethal blow - a wreck owes
   * nothing. This is the fraction that a truncation would have thrown away.
   */
  readonly carryMilli: number;
  /** False on a lethal blow, always. The property the module exists to hold. */
  readonly converted: boolean;
}

const NOTHING_INTEGER: IntegerStrikeResolution = Object.freeze({
  lethal: false,
  applied: 0,
  carryMilli: 0,
  converted: false,
});

/**
 * **The same frontier, for a simulation whose health is an integer.**
 *
 * ## Why the duel needed its own crossing
 *
 * {@link resolveStrike} hands back a possibly fractional `applied`, which is
 * exactly right for the hunt - a mob's health has always been a float. The
 * duel's is not: `snapshot-v3` decodes `health` with an INTEGER reader, so a
 * hull left on `359.6` could not be written down, and a format that cannot
 * store a state is a format that loses the fight on the next reconnection.
 *
 * Truncating the shot instead would be worse, and it is precisely what
 * `CLAUDE.md` forbids: `40 x 1,01` is `40,4`, and a heart that truncates to
 * `40` is an item whose effect the game silently deleted.
 *
 * ## So the remainder is CARRIED, on the hull that owes it
 *
 * The same answer the project already gives everywhere a fraction meets an
 * integer: `HuntClock` carries its sub-millisecond remainder, the hunt carries
 * `regenCarryMilli`, the duel's own cadence carries a residue in milli-ticks,
 * and {@link dotTickWithCarry} carries milli-damage-milliseconds. Three shots of
 * `40,4` take `40`, `40` and `41` - a hundred and twenty-one points, which is
 * exactly `121,2` truncated once at the end instead of three times along the way.
 *
 * The carry lives on the TARGET rather than on the shooter or the pair: it is
 * damage owed to THIS hull, and a wreck clears it because a hull that died owes
 * nothing to the next round.
 *
 * ## Lethality is still decided by comparison, never by conversion
 *
 * `damage + carry >= health x 1000` is a `bigint` comparison against a ceiling
 * that always fits. A blow of `10^43` milli and a blow of `40 400` milli end a
 * hull on 30 health identically, and neither is ever converted.
 *
 * @param remainingHealth whole health points. Fractional input is refused
 *   rather than rounded: it would mean the caller's hull already carries a
 *   fraction this function was written to keep out of it.
 */
export function resolveIntegerStrike(
  damageMilli: bigint,
  remainingHealth: number,
  carryMilli: number,
): IntegerStrikeResolution {
  if (!Number.isSafeInteger(remainingHealth)) {
    throw new RangeError(`remaining health must be a whole number, got ${remainingHealth}`);
  }
  if (!Number.isSafeInteger(carryMilli) || carryMilli < 0 || carryMilli >= DAMAGE_MILLI_PER_UNIT) {
    throw new RangeError(`a damage carry is in [0, ${DAMAGE_MILLI_PER_UNIT}), got ${carryMilli}`);
  }
  if (remainingHealth <= 0 || damageMilli <= 0n) {
    /*
     * A hull already down absorbs nothing, and a blow worth nothing takes
     * nothing. Neither emits, and neither disturbs the carry - which is why
     * this returns `carryMilli: 0` and the caller keeps what it had rather than
     * assigning: see the duel's `applyDamage`.
     */
    return NOTHING_INTEGER;
  }
  const total = damageMilli + BigInt(carryMilli);
  const ceilingMilli = BigInt(remainingHealth) * MILLI;
  if (total >= ceilingMilli) {
    // Lethal. How much larger it was is not a question anybody needs answered:
    // overkill has never been counted in either simulation.
    return { lethal: true, applied: remainingHealth, carryMilli: 0, converted: false };
  }
  /*
   * Proven small: strictly below a ceiling that is `remainingHealth x 1000`,
   * and `remainingHealth` is a safe integer. This conversion cannot lose a
   * digit, and it is the only one here.
   */
  return {
    lethal: false,
    applied: Number(total / MILLI),
    carryMilli: Number(total % MILLI),
    converted: true,
  };
}
